import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hello! I'm OL Consult AI Agent. I'm here to help you learn about our services in the Democratic Republic of Congo. How can I assist you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    // CEO Information
    if (lowerMessage.includes('ceo') || lowerMessage.includes('olivier') || lowerMessage.includes('shekinah') || lowerMessage.includes('founder')) {
      return "Our CEO is CHEKINAH OLIVIER, Founder & CEO, a visionary leader with extensive experience in DRC business development and international consulting. Under his leadership, OL-CONSULT has become a trusted partner for businesses seeking to invest in the Democratic Republic of Congo.";
    }

    // Services
    if (lowerMessage.includes('service') || lowerMessage.includes('what do you do')) {
      return "We offer comprehensive business consulting services including: Company Formation, Legal Protection & Investment Guarantees, Business Consulting & Strategy, Market Entry Planning, and specialized guidance in Mining, Agriculture, Infrastructure, Renewable Energy, and Telecommunications sectors.";
    }

    // Mining
    if (lowerMessage.includes('mining') || lowerMessage.includes('cobalt') || lowerMessage.includes('copper')) {
      return "The DRC holds 70% of global cobalt reserves and vast deposits of copper, gold, and other valuable minerals. We guide investors through regulatory requirements, forge strategic partnerships, and promote sustainable mining practices. Our team has deep expertise in the mining sector.";
    }

    // Agriculture
    if (lowerMessage.includes('agricult') || lowerMessage.includes('farming')) {
      return "The DRC has 80 million hectares of arable land with favorable climate conditions. We help investors connect with local partners, secure land acquisition, establish logistics, and access markets for agricultural investments and agro-processing ventures.";
    }

    // Infrastructure
    if (lowerMessage.includes('infrastructure') || lowerMessage.includes('construction')) {
      return "We assist investors in infrastructure projects including road networks, ports, railways, and energy infrastructure. Our team helps identify viable projects, secure financing, and navigate regulatory requirements for successful project implementation.";
    }

    // Energy
    if (lowerMessage.includes('energy') || lowerMessage.includes('solar') || lowerMessage.includes('renewable')) {
      return "The DRC has 100 GW of hydroelectric potential and abundant solar and wind resources. We help investors tap into renewable energy opportunities, including project identification, regulatory compliance, and partnership development in the clean energy sector.";
    }

    // Telecommunications
    if (lowerMessage.includes('telecom') || lowerMessage.includes('digital') || lowerMessage.includes('technology')) {
      return "The DRC's telecommunications sector is experiencing rapid growth with increasing mobile penetration and digital service demand. We help investors capitalize on opportunities in mobile networks, digital services, and telecommunications infrastructure.";
    }

    // Investment
    if (lowerMessage.includes('invest') || lowerMessage.includes('opportunity')) {
      return "The DRC offers exceptional investment opportunities with vast natural resources, a growing market of 90M+ people, and immense development potential. We provide end-to-end support from market entry to operational success. Would you like to know about specific sectors?";
    }

    // Contact
    if (lowerMessage.includes('contact') || lowerMessage.includes('reach') || lowerMessage.includes('email') || lowerMessage.includes('phone')) {
      return "You can reach us at: Email: contact@olconsult.cd | Phone: +243 XXX XXX XXX | Location: Kinshasa, DRC. We're available to discuss your business needs and investment opportunities.";
    }

    // DRC Information
    if (lowerMessage.includes('drc') || lowerMessage.includes('congo') || lowerMessage.includes('kinshasa')) {
      return "The Democratic Republic of Congo is rich in natural resources with 70% of global cobalt reserves, 100 GW hydroelectric potential, 80M hectares of arable land, and a population of 90M+. It offers tremendous opportunities for investors across multiple sectors.";
    }

    // Default response
    return "I can help you with information about our services, business sectors (Mining, Agriculture, Infrastructure, Renewable Energy, Telecommunications), investment opportunities in DRC, and how to contact us. What would you like to know?";
  };

  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;

    const userMessage = { type: 'user', text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate bot typing
    setTimeout(() => {
      const botResponse = { type: 'bot', text: getBotResponse(inputValue) };
      setMessages(prev => [...prev, botResponse]);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 z-50 group"
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl z-50 flex flex-col overflow-hidden border-2 border-blue-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">OL Consult AI Agent</h3>
                <p className="text-xs text-blue-100">CEO: CHEKINAH OLIVIER</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        message.type === 'user'
                          ? 'bg-blue-900 text-white rounded-br-none'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-sm'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{message.text}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-900 transition-colors"
                  />
                  <Button
                    onClick={handleSendMessage}
                    className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default ChatBot;
